/**
 * The time zone catalog behind every embed's zone picker.
 *
 * The pickers used to offer a hand-written handful of U.S. zones (four in the
 * events/appointments embeds, seven in the schedule), so a visitor in London or
 * Sydney had no way to see class times in their own wall clock unless the
 * studio happened to sit in their zone. The full IANA list comes from the
 * engine via `Intl.supportedValuesOf`, so it stays current with the browser's
 * tzdata instead of a list we would have to maintain.
 *
 * U.S. zones stay at the top, under their conventional names, because they are
 * what almost every visitor picks — a 400-entry alphabetical list that opens on
 * "Abidjan" would be a regression for them.
 */

/** A zone plus the label shown for it in a picker. */
export interface TimezoneOption {
  tz: string;
  label: string;
}

/** One `<optgroup>` worth of zones. */
export interface TimezoneGroup {
  label: string;
  zones: TimezoneOption[];
}

/**
 * The U.S. zones, in the conventional east→west order, with the names people
 * actually use for them. Arizona and Hawaii are listed in their own right
 * because they don't observe DST, so their abbreviation and offset diverge
 * from their neighbours for half the year.
 */
export const US_TIMEZONES: { tz: string; name: string }[] = [
  { tz: "America/New_York", name: "Eastern" },
  { tz: "America/Chicago", name: "Central" },
  { tz: "America/Denver", name: "Mountain" },
  { tz: "America/Phoenix", name: "Arizona" },
  { tz: "America/Los_Angeles", name: "Pacific" },
  { tz: "America/Anchorage", name: "Alaska" },
  { tz: "Pacific/Honolulu", name: "Hawaii" },
];

const US_ZONE_NAMES = new Map(US_TIMEZONES.map((z) => [z.tz, z.name]));

/**
 * IANA regions in the order they're offered. The Americas come first (see the
 * file header); the rest are alphabetical, with the region-less zones (UTC,
 * Etc/*) last since nobody browses to them.
 */
const REGION_ORDER = [
  "America",
  "Africa",
  "Antarctica",
  "Arctic",
  "Asia",
  "Atlantic",
  "Australia",
  "Europe",
  "Indian",
  "Pacific",
];

const REGION_LABELS: Record<string, string> = {
  America: "Americas",
  Atlantic: "Atlantic",
  Indian: "Indian Ocean",
};

const abbrevCache = new Map<string, string>();

/**
 * Short label for a zone right now, e.g. "EDT", "GMT+11". Returns the zone
 * itself when the engine can't produce one, so callers can tell the difference
 * and omit it rather than printing a raw IANA name in a narrow column.
 */
export function tzAbbrev(tz: string): string {
  if (!tz) return "";
  const cached = abbrevCache.get(tz);
  if (cached !== undefined) return cached;
  let abbrev = tz;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      timeZoneName: "short",
    }).formatToParts(new Date());
    abbrev = parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    // An unusable zone name makes Intl throw; the caller's fallback applies.
  }
  abbrevCache.set(tz, abbrev);
  return abbrev;
}

/**
 * Human name for a zone: the U.S. preset name when it is one, else the IANA
 * city segment ("America/Argentina/Buenos_Aires" → "Buenos Aires").
 */
export function tzFriendlyName(tz: string): string {
  const preset = US_ZONE_NAMES.get(tz);
  if (preset) return preset;
  return tz.split("/").pop()?.replace(/_/g, " ") || tz;
}

/** A zone's picker label: friendly name + current abbreviation, e.g.
 * "Eastern (EDT)". Drops the abbreviation when the engine can't resolve one. */
export function tzOptionLabel(tz: string): string {
  const abbrev = tzAbbrev(tz);
  const name = tzFriendlyName(tz);
  return abbrev && abbrev !== tz ? `${name} (${abbrev})` : name;
}

/**
 * Every zone the engine knows about.
 *
 * `Intl.supportedValuesOf` landed in Safari 15.4 / Firefox 93; on anything
 * older this returns nothing and the picker falls back to the U.S. presets it
 * has always offered, plus whatever the caller pins. That is the old behaviour,
 * not a new dead end.
 */
function supportedZones(): string[] {
  try {
    const supportedValuesOf = (
      Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
      }
    ).supportedValuesOf;
    return supportedValuesOf?.("timeZone") ?? [];
  } catch {
    return [];
  }
}

let cachedGroups: TimezoneGroup[] | null = null;

/**
 * The full catalog, grouped for a `<select>`: U.S. zones first, then the
 * Americas, then the rest of the world.
 *
 * Built once and cached — labelling ~450 zones means ~450 `Intl.DateTimeFormat`
 * constructions, which is fine once on first open but not on every Lit render.
 */
function catalogGroups(): TimezoneGroup[] {
  if (cachedGroups) return cachedGroups;

  const presets = new Set(US_TIMEZONES.map((z) => z.tz));
  const byRegion = new Map<string, TimezoneOption[]>();

  for (const tz of supportedZones()) {
    if (presets.has(tz)) continue;
    const region = tz.includes("/") ? tz.split("/")[0] : "Other";
    const bucket = byRegion.get(region) ?? [];
    bucket.push({ tz, label: tzOptionLabel(tz) });
    byRegion.set(region, bucket);
  }

  const groups: TimezoneGroup[] = [
    {
      label: "United States",
      zones: US_TIMEZONES.map((z) => ({
        tz: z.tz,
        label: tzOptionLabel(z.tz),
      })),
    },
  ];

  const regions = [
    ...REGION_ORDER.filter((r) => byRegion.has(r)),
    // Anything the engine knows that this list doesn't — new regions, or the
    // region-less UTC/Etc zones — sorted after the named ones rather than
    // dropped, so "all time zones" stays true without another edit here.
    ...[...byRegion.keys()].filter((r) => !REGION_ORDER.includes(r)).sort(),
  ];

  for (const region of regions) {
    const zones = byRegion.get(region);
    if (!zones?.length) continue;
    groups.push({
      label: REGION_LABELS[region] ?? region,
      zones: zones.sort((a, b) => a.label.localeCompare(b.label)),
    });
  }

  cachedGroups = groups;
  return groups;
}

/**
 * Picker groups, with `pinned` zones repeated in a short group at the very top.
 *
 * Pinning is what keeps the common case one glance away: the studio's own zone
 * and the visitor's own zone are the two anyone actually wants, and hunting for
 * them inside a 450-entry list would be worse than the four-entry picker this
 * replaces. Duplicating them lower down is deliberate and conventional — a
 * `<select>` matches its value against the first option carrying it.
 *
 * @param pinned zones to surface first, in order; blanks and repeats are dropped
 */
export function timezoneGroups(
  pinned: { tz: string; label: string }[] = [],
): TimezoneGroup[] {
  const seen = new Set<string>();
  const zones: TimezoneOption[] = [];
  for (const { tz, label } of pinned) {
    if (!tz || seen.has(tz)) continue;
    seen.add(tz);
    const abbrev = tzAbbrev(tz);
    zones.push({
      tz,
      // "Studio time — Eastern (EDT)": the pinned row has to say which zone it
      // actually is, or the visitor can't tell whether it differs from theirs.
      label: `${label} — ${tzOptionLabel(tz)}${abbrev === tz ? ` (${tz})` : ""}`,
    });
  }
  const catalog = catalogGroups();
  return zones.length ? [{ label: "Suggested", zones }, ...catalog] : catalog;
}

/** Test seam: drop the memoized catalog/abbreviations. */
export function resetTimezoneCatalogCache(): void {
  cachedGroups = null;
  abbrevCache.clear();
}
